import React, { useState } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useCookieConsent, CookieConsent } from '../../contexts/CookieConsentContext';
import { X, Cookie, Settings } from 'lucide-react';

export const CookieBanner: React.FC = () => {
  const { t } = useTranslation();
  const { showBanner, acceptAll, rejectAll, savePreferences } = useCookieConsent();
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookieConsent>({
    necessary: true,
    analytics: false
  });

  if (!showBanner) return null;

  const handlePreferenceChange = (category: keyof CookieConsent, value: boolean) => {
    if (category === 'necessary') return;
    setPreferences(prev => ({ ...prev, [category]: value }));
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  return (
    <motion.div 
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-primary/20 shadow-modern z-50"
    >
      <div className="max-w-7xl mx-auto p-4">
        {!showDetails ? (
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center flex-shrink-0">
                <Cookie className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">{t('cookieBanner.title')}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {t('cookieBanner.description')}{' '}
                  <button 
                    onClick={() => window.open('/cookie-policy', '_blank')}
                    className="text-gradient font-medium hover:underline"
                  >
                    {t('cookieBanner.cookiePolicyLink')}
                  </button>.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowDetails(true)}
                className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100/80 border border-gray-300/50 rounded-full hover:bg-gray-200/80 transition-all duration-200"
              >
                <Settings className="h-4 w-4" />
                {t('cookieBanner.buttons.viewDetails')}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={acceptAll}
                className="px-6 py-2 text-sm font-medium text-white bg-gradient-primary border border-primary rounded-full hover:shadow-button transition-all duration-200"
              >
                {t('cookieBanner.buttons.acceptAll')}
              </motion.button>
            </div>
          </div>
        ) : (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="space-y-4"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{t('cookieBanner.preferences.title')}</h3>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowDetails(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-all duration-200"
              >
                <X className="h-5 w-5" />
              </motion.button>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl border border-primary/10">
                <div>
                  <h4 className="font-medium text-gray-900">{t('cookieBanner.preferences.necessary.title')}</h4>
                  <p className="text-sm text-gray-600">{t('cookieBanner.preferences.necessary.description')}</p>
                </div>
                <div className="w-12 h-6 bg-primary rounded-full flex items-center justify-end px-1">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-gray-50/80 rounded-xl border border-gray-200/50">
                <div>
                  <h4 className="font-medium text-gray-900">{t('cookieBanner.preferences.analytics.title')}</h4>
                  <p className="text-sm text-gray-600">{t('cookieBanner.preferences.analytics.description')}</p>
                </div>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handlePreferenceChange('analytics', !preferences.analytics)}
                  className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors duration-200 ${
                    preferences.analytics ? 'bg-primary justify-end' : 'bg-gray-300 justify-start'
                  }`}
                >
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </motion.button>
              </div>
            </div>
            
            <div className="flex justify-center gap-3 pt-4 border-t border-primary/10">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={rejectAll}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100/80 border border-gray-300/50 rounded-full hover:bg-gray-200/80 transition-all duration-200"
              >
                {t('cookieBanner.buttons.rejectAll')}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSavePreferences}
                className="px-6 py-2 text-sm font-medium text-white bg-gradient-primary border border-primary rounded-full hover:shadow-button transition-all duration-200"
              >
                {t('cookieBanner.buttons.savePreferences')}
              </motion.button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};